import {AnchorHTMLAttributes, HTMLAttributes, ImgHTMLAttributes, PropsWithChildren} from 'react';
import {
  Box,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useNavigate, useRouter} from '@tanstack/react-router';

// Maps the HTML elements emitted by compiled MDX onto MUI components, so authored
// content stays plain markdown (portable to Docusaurus) and the styling lives here.

// `id` is injected by rehype-slug so in-page anchor links (e.g. #section-title) resolve.
// scrollMarginTop keeps anchored headings clear of the fixed app bar.
type HeadingProps = PropsWithChildren<{id?: string}>;

const Heading1: React.FC<HeadingProps> = ({id, children}) => (
  <Typography id={id} variant="h4" component="h1" sx={{fontWeight: 600, mt: 0, mb: 2, scrollMarginTop: 72}}>
    {children}
  </Typography>
);

const Heading2: React.FC<HeadingProps> = ({id, children}) => (
  <Typography
    id={id}
    variant="h5"
    component="h2"
    sx={{fontWeight: 600, mt: 5, mb: 1.5, pt: 2, borderTop: 1, borderColor: 'divider', scrollMarginTop: 72}}
  >
    {children}
  </Typography>
);

const Heading3: React.FC<HeadingProps> = ({id, children}) => (
  <Typography id={id} variant="h6" component="h3" sx={{fontWeight: 600, mt: 3, mb: 1, scrollMarginTop: 72}}>
    {children}
  </Typography>
);

const Heading4: React.FC<HeadingProps> = ({id, children}) => (
  <Typography
    id={id}
    variant="subtitle1"
    component="h4"
    sx={{fontWeight: 600, mt: 2, mb: 0.5, scrollMarginTop: 72}}
  >
    {children}
  </Typography>
);

const Heading5: React.FC<HeadingProps> = ({id, children}) => (
  <Typography
    id={id}
    variant="subtitle2"
    component="h5"
    sx={{fontWeight: 600, mt: 2, mb: 0.5, scrollMarginTop: 72}}
  >
    {children}
  </Typography>
);

const Paragraph: React.FC<PropsWithChildren> = ({children}) => (
  <Typography variant="body1" component="p" sx={{my: 1.5, lineHeight: 1.7}}>
    {children}
  </Typography>
);

// Internal manual links are authored as `NN-name` chapter references (with or without an
// `.md`/`.mdx` extension and an optional `#anchor`). The numeric prefix is dropped from the
// app route so URLs stay clean — rewrite them to `/name` (e.g. `02-getting-started.mdx#x` -> `/getting-started#x`).
// Leave plain `#anchor` links and external URLs to native behaviour (headings carry ids).
const CHAPTER_LINK = /^\.?\/?\d{2}-([\w-]+)(?:\.mdx?)?(#.+)?$/;

const Anchor: React.FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({href, children}) => {
  const navigate = useNavigate();
  const router = useRouter();
  const isExternal = !!href && /^https?:\/\//.test(href);
  const chapter = isExternal ? null : href?.match(CHAPTER_LINK);

  if (chapter) {
    const slug = chapter[1];
    const anchor = chapter[2] ?? '';
    const hash = anchor ? anchor.slice(1) : undefined;
    // Real href (with the app basepath) so middle/ctrl-click and "open in new tab" resolve correctly…
    const base = router.basepath && router.basepath !== '/' ? router.basepath.replace(/\/+$/, '') : '';
    return (
      <Link
        href={`${base}/${slug}${anchor}`}
        color="primary"
        underline="hover"
        onClick={event => {
          // …but a plain left-click navigates client-side, so it always resolves inside this app
          // with the correct basepath instead of a fresh request to the origin root.
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }
          event.preventDefault();
          void navigate({to: '/$slug', params: {slug}, hash});
        }}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      href={href}
      color="primary"
      underline="hover"
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
    >
      {children}
    </Link>
  );
};

const UnorderedList: React.FC<PropsWithChildren> = ({children}) => (
  <Box component="ul" sx={{pl: 3, my: 1.5}}>
    {children}
  </Box>
);

const OrderedList: React.FC<PropsWithChildren> = ({children}) => (
  <Box component="ol" sx={{pl: 3, my: 1.5}}>
    {children}
  </Box>
);

const ListItem: React.FC<PropsWithChildren> = ({children}) => (
  <Typography
    component="li"
    variant="body1"
    sx={{
      mb: 0.5,
      lineHeight: 1.7,
      // In "loose" markdown lists (items separated by blank lines), each item's text is
      // wrapped in a <p>. Neutralise that paragraph's vertical margin so loose lists don't
      // render with large gaps between items.
      '& > p': {my: 0},
    }}
  >
    {children}
  </Typography>
);

const Blockquote: React.FC<PropsWithChildren> = ({children}) => (
  <Box
    component="aside"
    sx={{
      borderLeft: 4,
      borderColor: 'primary.main',
      bgcolor: 'action.hover',
      px: 2,
      py: 0.5,
      my: 2,
      borderRadius: 1,
      '& p': {my: 1},
    }}
  >
    {children}
  </Box>
);

const InlineCode: React.FC<PropsWithChildren> = ({children}) => (
  <Box
    component="code"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.875em',
      bgcolor: 'action.selected',
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
    }}
  >
    {children}
  </Box>
);

const Pre: React.FC<PropsWithChildren> = ({children}) => (
  <Box
    component="pre"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.85rem',
      bgcolor: 'background.default',
      border: 1,
      borderColor: 'divider',
      p: 2,
      my: 2,
      borderRadius: 1,
      overflowX: 'auto',
      '& code': {bgcolor: 'transparent', p: 0},
    }}
  >
    {children}
  </Box>
);

const Image: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({src, alt}) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    loading="lazy"
    sx={{
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      my: 2,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
    }}
  />
);

const Hr: React.FC = () => <Divider sx={{my: 4}} />;

const ThematicTable: React.FC<PropsWithChildren> = ({children}) => (
  <TableContainer component={Paper} variant="outlined" sx={{my: 2}}>
    <Table size="small">{children}</Table>
  </TableContainer>
);

const Head: React.FC<PropsWithChildren> = ({children}) => <TableHead>{children}</TableHead>;

const Body: React.FC<PropsWithChildren> = ({children}) => <TableBody>{children}</TableBody>;

const HeaderCell: React.FC<PropsWithChildren> = ({children}) => (
  <TableCell sx={{fontWeight: 600}}>{children}</TableCell>
);

const BodyCell: React.FC<PropsWithChildren> = ({children}) => <TableCell>{children}</TableCell>;

const Row: React.FC<HTMLAttributes<HTMLTableRowElement>> = ({children}) => <TableRow>{children}</TableRow>;

export const mdxComponents = {
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
  h4: Heading4,
  h5: Heading5,
  h6: Heading5,
  p: Paragraph,
  a: Anchor,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  blockquote: Blockquote,
  code: InlineCode,
  pre: Pre,
  img: Image,
  hr: Hr,
  table: ThematicTable,
  thead: Head,
  tbody: Body,
  tr: Row,
  th: HeaderCell,
  td: BodyCell,
};
